/**
 * CTA Banner snippet-author runtime.
 *
 * The band renders complete from SSR — layout, scrim, badge, copy, and CTAs
 * need no JavaScript. This runtime only adds the two SDK-coupled behaviors:
 *   - CTA click analytics through __spectrumAi.snippet.bind
 *   - Studio live-preview (applyVariant) so copy/alignment edits reflect
 *     without a reload
 *
 * Container-scoped: every read/write goes through the instance container, so
 * multi-render pages never collide. Visibility is enforced upstream by the
 * wrapper's display:none gate.
 */
;(() => {
  const SNIPPET_ID = 'whfz0ift'
  const ROOT_SELECTOR = `.sai-${SNIPPET_ID}`

  /* ────────── applyVariant — live preview (Studio) ────────── */

  function applyVariant(node, content) {
    if (content == null || typeof content !== 'object') return
    const root = node.querySelector(ROOT_SELECTOR)
    if (!root) return

    // Alignment (desktop + mobile override). The SSR markup sets these classes;
    // re-apply them here so Studio live edits to either dropdown reflect without
    // a reload. The mobile class only takes effect under the mobile media query.
    for (const a of ['left', 'center', 'right']) {
      root.classList.remove(`sai-whfz0ift--align-${a}`, `sai-whfz0ift--align-m-${a}`)
    }
    const alignH = content.content_align_h || 'left'
    root.classList.add(`sai-whfz0ift--align-${alignH}`)
    const alignM = content.content_align_h_mobile
    if (alignM && alignM !== 'inherit') {
      root.classList.add(`sai-whfz0ift--align-m-${alignM}`)
    }

    setText(root, '.sai-whfz0ift__subtitle', content.subtitle)
    setText(root, '.sai-whfz0ift__title-accent', content.title_accent)
    setText(root, '.sai-whfz0ift__badge span', content.badge_text)

    const primary = root.querySelector('.sai-whfz0ift__cta--primary')
    if (primary && typeof content.primary_cta_text === 'string')
      primary.textContent = content.primary_cta_text
    if (primary && typeof content.primary_cta_url === 'string')
      primary.setAttribute('href', content.primary_cta_url)
    const secondary = root.querySelector('.sai-whfz0ift__cta--secondary')
    if (secondary && typeof content.secondary_cta_text === 'string')
      secondary.textContent = content.secondary_cta_text
    if (secondary && typeof content.secondary_cta_url === 'string')
      secondary.setAttribute('href', content.secondary_cta_url)
  }

  function setText(root, selector, value) {
    if (typeof value !== 'string') return
    const el = root.querySelector(selector)
    if (el) el.textContent = value
  }

  /* ────────── Boot ────────── */

  function boot() {
    const snippetApi = window.__spectrumAi?.snippet
    if (!snippetApi || typeof snippetApi.bind !== 'function') return
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )
    for (const node of containers) {
      const handle = snippetApi.bind(node, ({ variants, currentVariantId }) => {
        const variant = variants.find((v) => v.variantId === currentVariantId)
        if (variant?.content) applyVariant(node, variant.content)
      })
      wireCtas(node, handle)
    }
  }

  function wireCtas(node, handle) {
    if (!handle || typeof handle.track !== 'function') return
    for (const cta of node.querySelectorAll('[data-sai-cta]')) {
      cta.addEventListener('click', () => {
        handle.track(`cta_banner:${cta.getAttribute('data-sai-cta')}_cta_click`, {
          cta_url: cta.getAttribute('href') || '',
          cta_text: (cta.textContent || '').trim(),
        })
      })
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot)
  } else {
    boot()
  }
})()
